// HR Settings & Policy — WP plugin parity (Screen 12 — admin).
// 5 sections (Office WiFi/IP, Working Hours, Leave Entitlements, Public
// Holidays bulk-sync, SMM API key). Payroll-only fields excluded per scope.

import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, CardHeader, Field } from "../../Components/ui";

export default function SettingsPage() {
  const role = localStorage.getItem("role");
  const toast = useToast();
  const [attSettings, setAttSettings] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [bulkText, setBulkText] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  useShellHeader({ title: "HR Settings & Policy" }, []);

  useEffect(() => {
    if (role !== "SUPER_ADMIN") return;
    Promise.all([
      api("/attendance/settings").catch(() => ({})),
      api("/leaves/policy").catch(() => ({})),
    ]).then(([s, p]) => {
      setAttSettings(s?.settings || s || {});
      setPolicy(p?.policy || {});
    }).catch((e) => toast({ title: e.message, status: "error" }));
    // eslint-disable-next-line
  }, []);

  if (role !== "SUPER_ADMIN") {
    return <Card padding="lg"><div style={{ fontWeight: 600 }}>Admin only.</div></Card>;
  }
  if (!attSettings || !policy) {
    return <div style={{ padding: 16, color: "var(--text-tertiary)" }}>Loading…</div>;
  }

  async function saveAll() {
    setSaving(true);
    try {
      await Promise.all([
        api("/attendance/settings", { method: "PUT", body: JSON.stringify(attSettings) }),
        api("/leaves/policy",       { method: "PUT", body: JSON.stringify(policy) }),
      ]);
      toast({ title: "Settings saved.", status: "success" });
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
    finally { setSaving(false); }
  }

  async function syncHolidays() {
    if (!bulkText.trim()) { toast({ title: "Paste holidays first.", status: "warning" }); return; }
    setSyncing(true);
    try {
      const r = await api("/hr/holidays/sync", { method: "POST", body: JSON.stringify({ text: bulkText }) });
      toast({
        title: `Sync done — ${r.inserted} added, ${r.updated} already there, ${r.skipped} skipped`,
        status: r.errors?.length ? "warning" : "success",
        duration: 6000,
      });
      if (r.errors?.length) console.warn("Holiday sync errors:", r.errors);
      setBulkText("");
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
    finally { setSyncing(false); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>📝 HR Settings & Policy</h2>

      {/* ── Section 1: Office WiFi / IP ─────────────────────────── */}
      <Card padding="none">
        <CardHeader title="Office WiFi / IP Check-in" />
        <Section helper="Employees can only check in when on office WiFi. Admins always bypass this gate.">
          <Two>
            <Field label="WIFI LABEL (SHOWN TO EMPLOYEE)">
              <input style={inputStyle} value={attSettings.officeWifiLabel || ""} onChange={(e) => setAttSettings({ ...attSettings, officeWifiLabel: e.target.value })} />
            </Field>
            <Field label="STRICT MODE">
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-secondary)" }}>
                <input type="checkbox" checked={!!attSettings.strictWifi} onChange={(e) => setAttSettings({ ...attSettings, strictWifi: e.target.checked })} />
                Block check-in if IP doesn't match
              </label>
            </Field>
          </Two>
          <Field label="ALLOWED OFFICE IPS (ONE PER LINE, CIDR SUPPORTED E.G. 203.0.113.0/24)">
            <textarea
              style={{ ...inputStyle, minHeight: 100, resize: "vertical", fontFamily: "monospace" }}
              value={(attSettings.officeIps || []).join("\n")}
              onChange={(e) => setAttSettings({ ...attSettings, officeIps: e.target.value.split(/\r?\n/).map((s) => s.trim()).filter(Boolean) })}
            />
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const r = await api("/attendance/my-ip");
                    const ip = r.clientIp;
                    if (!ip) { toast({ title: "Could not detect your IP", status: "warning" }); return; }
                    const cur = attSettings.officeIps || [];
                    if (cur.includes(ip)) {
                      toast({ title: `${ip} is already in the list`, status: "info" });
                      return;
                    }
                    setAttSettings({ ...attSettings, officeIps: [...cur, ip] });
                    toast({ title: `Added ${ip} — click "Save settings" below to apply`, status: "success", duration: 6000 });
                  } catch (e) {
                    toast({ title: e.data?.message || e.message || "Could not detect your IP", status: "error" });
                  }
                }}
                style={{
                  background: "var(--accent-primary)", color: "white", border: "none",
                  padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >
                📍 Use my current IP
              </button>
              <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                Open this page from your office Wi-Fi → click the button → save. Adds the office's public egress IP automatically.
              </span>
            </div>
          </Field>
        </Section>
      </Card>

      {/* ── Section 2: Working Hours & Attendance Rules ─────────── */}
      <Card padding="none">
        <CardHeader title="🕐 Working Hours & Attendance Rules" />
        <Section helper="Per Quantiv Media HR Policy §2.2 & §2.3 — Mon–Fri, 10 AM–7 PM, late after 10:15 AM.">
          <Two>
            <Field label="WORK DAY STARTS">
              <input type="time" style={inputStyle} value={attSettings.workDayStarts || "10:00"} onChange={(e) => setAttSettings({ ...attSettings, workDayStarts: e.target.value })} />
            </Field>
            <Field label="WORK DAY ENDS">
              <input type="time" style={inputStyle} value={attSettings.workDayEnds || "19:00"} onChange={(e) => setAttSettings({ ...attSettings, workDayEnds: e.target.value })} />
            </Field>
          </Two>
          <Two>
            <Field label="LATE MARK CUTOFF" helper="Arrivals after this = Late (10:15 per §2.3)">
              <input type="time" style={inputStyle} value={attSettings.lateMarkCutoff || "10:15"} onChange={(e) => setAttSettings({ ...attSettings, lateMarkCutoff: e.target.value })} />
            </Field>
            <Field label="LATE THRESHOLD — FALLBACK (MINS AFTER SHIFT)" helper="Used when no shift is assigned">
              <input type="number" min={0} max={120} style={inputStyle} value={attSettings.lateThreshold ?? 15} onChange={(e) => setAttSettings({ ...attSettings, lateThreshold: parseInt(e.target.value, 10) || 0 })} />
            </Field>
          </Two>
          <Two>
            <Field label="LUNCH BREAK STARTS">
              <input type="time" style={inputStyle} value={attSettings.lunchStarts || "13:00"} onChange={(e) => setAttSettings({ ...attSettings, lunchStarts: e.target.value })} />
            </Field>
            <Field label="LUNCH BREAK ENDS">
              <input type="time" style={inputStyle} value={attSettings.lunchEnds || "13:40"} onChange={(e) => setAttSettings({ ...attSettings, lunchEnds: e.target.value })} />
            </Field>
          </Two>
          <Two>
            <Field label="LATES BEFORE 1 DAY SALARY CUT" helper="§2.3.1 — 3 lates/month = 1 day deducted">
              <input type="number" min={0} max={30} style={inputStyle} value={attSettings.latesBefore1DayCut ?? 3} onChange={(e) => setAttSettings({ ...attSettings, latesBefore1DayCut: parseInt(e.target.value, 10) || 0 })} />
            </Field>
            <Field label="ALLOW CHECK-IN (MINS BEFORE SHIFT)" helper="How early the Check In button activates">
              <input type="number" min={0} max={240} style={inputStyle} value={attSettings.allowCheckinMinsBeforeShift ?? 0} onChange={(e) => setAttSettings({ ...attSettings, allowCheckinMinsBeforeShift: parseInt(e.target.value, 10) || 0 })} />
            </Field>
          </Two>
        </Section>
      </Card>

      {/* ── Section 3: Leave Entitlements ──────────────────────── */}
      <Card padding="none">
        <CardHeader title="📅 Leave Entitlements (Annual Defaults)" />
        <Section helper="Per HR Policy §3. Company-wide defaults. Override per-staff in Leave Balances.">
          <Two>
            <PolicyField label="ANNUAL LEAVE (DAYS/YEAR)"      k="annual"            policy={policy} setPolicy={setPolicy} helper="§3.2 — 7 days, accrues 0.53/month" />
            <PolicyField label="SICK LEAVE (DAYS/YEAR)"        k="sick"              policy={policy} setPolicy={setPolicy} helper="§3.3 — paid" />
          </Two>
          <Two>
            <PolicyField label="CASUAL LEAVE (DAYS/YEAR)"      k="casual"            policy={policy} setPolicy={setPolicy} helper="§3.4 — apply 2 days prior" />
            <PolicyField label="WFH DAYS (PER MONTH)"          k="wfh_per_month"     policy={policy} setPolicy={setPolicy} helper="§13.1 — for confirmed staff" />
          </Two>
          <Two>
            <PolicyField label="WFH DAYS — PROBATION (TOTAL)"  k="wfh_probation_total" policy={policy} setPolicy={setPolicy} helper="§13.1 — max 7 across 3-mo probation" />
            <PolicyField label="MARRIAGE LEAVE (DAYS)"         k="marriage"          policy={policy} setPolicy={setPolicy} helper="§3.8 — paid" />
          </Two>
          <Two>
            <PolicyField label="BEREAVEMENT LEAVE (DAYS)"      k="bereavement"       policy={policy} setPolicy={setPolicy} helper="§3.8 — immediate family" />
            <PolicyField label="EXAMINATION LEAVE (DAYS)"      k="examination"       policy={policy} setPolicy={setPolicy} helper="§3.8 — approved studies" />
          </Two>
          <Two>
            <PolicyField label="PATERNITY LEAVE (DAYS)"        k="paternity"         policy={policy} setPolicy={setPolicy} helper="§3.5 — male employees" />
            <PolicyField label="MATERNITY LEAVE (DAYS)"        k="maternity"         policy={policy} setPolicy={setPolicy} helper="§3.5 — admin override of statutory floor" />
          </Two>
          <Two>
            <PolicyField label="CASUAL LEAVE — ADVANCE NOTICE (DAYS)"      k="casual_advance_notice"         policy={policy} setPolicy={setPolicy} helper="§3.4 — Apply at least N days before" />
            <PolicyField label="SANDWICH HOLIDAY — ADVANCE NOTICE (DAYS)"  k="sandwich_holiday_advance_notice" policy={policy} setPolicy={setPolicy} helper="§3.7" />
          </Two>
        </Section>
      </Card>

      {/* ── Section 4: Bulk holiday sync ──────────────────────── */}
      <Card padding="none">
        <CardHeader title="⭐ Public Holidays — Bulk Sync (§3.6)" />
        <Section helper={`Format: "Holiday Name|YYYY-MM-DD" — one per line. Diwali has 7 days, add each separately.`}>
          <textarea
            style={{ ...inputStyle, minHeight: 200, resize: "vertical", fontFamily: "monospace", fontSize: 12 }}
            placeholder={`Gudi Padwa|2026-03-20\nIndependence Day|2026-08-15\nDiwali Day 1|2026-11-08\n...`}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />
          <button onClick={syncHolidays} style={btnPrimary} disabled={syncing}>{syncing ? "Syncing…" : "🔄 Sync to Holidays Module"}</button>
        </Section>
      </Card>

      {/* ── Save All ─────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button onClick={saveAll} style={{ ...btnPrimary, padding: "12px 28px", fontSize: 14 }} disabled={saving}>
          {saving ? "Saving…" : "💾 Save All Settings"}
        </button>
      </div>
    </div>
  );
}

function PolicyField({ label, k, policy, setPolicy, helper }) {
  return (
    <Field label={label} helper={helper}>
      <input type="number" min={0} max={365} step={0.5} style={inputStyle}
        value={policy[k] ?? 0}
        onChange={(e) => setPolicy({ ...policy, [k]: parseFloat(e.target.value) || 0 })}
      />
    </Field>
  );
}

function Section({ helper, children }) {
  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
      {helper && <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 4 }}>{helper}</div>}
      {children}
    </div>
  );
}
function Two({ children }) { return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>{children}</div>; }
const inputStyle = {
  border: "1px solid var(--border-default)", borderRadius: 8,
  padding: "8px 10px", fontSize: 13, background: "var(--bg-surface)",
  color: "var(--text-primary)", width: "100%", fontFamily: "inherit",
};
const btnPrimary = {
  background: "var(--accent-primary)", color: "white", border: "none",
  padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
  alignSelf: "flex-start",
};
